import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert, 
  Spinner,
  Badge
} from 'react-bootstrap';
import { MessageSquare, Server, Send, CheckCircle, AlertTriangle, Save, Phone } from 'react-feather';
import SettingsService from '../../../services/SettingsService';

const TwilioSettings = ({ user }) => {
  const [twilioSettings, setTwilioSettings] = useState({
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_phone_number: ''
  });
  const [testPhone, setTestPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await SettingsService.getTwilioSettings();
      if (response.success) {
        setTwilioSettings(prev => ({
          ...prev,
          ...response.settings,
          // Mask auth token if it exists
          twilio_auth_token: response.settings.twilio_auth_token ? '••••••••' : ''
        }));
      }
    } catch (err) {
      setError('Failed to load Twilio settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTwilioSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await SettingsService.updateTwilioSettings(twilioSettings);
      
      if (response.success) {
        setSuccess('Twilio settings saved successfully!');
        // Re-mask the auth token
        setTwilioSettings(prev => ({
          ...prev,
          twilio_auth_token: prev.twilio_auth_token && prev.twilio_auth_token !== '••••••••' 
            ? '••••••••' 
            : prev.twilio_auth_token
        }));
      } else {
        setError(response.error || 'Failed to save settings');
      }
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSms = async () => {
    if (!testPhone) {
      setError('Please enter a test phone number');
      return;
    }

    // Validate phone format
    const phoneClean = testPhone.replace(/\s/g, '');
    if (!/^\+?[1-9]\d{7,14}$/.test(phoneClean)) {
      setError('Please enter a valid phone number with country code (e.g. +1234567890)');
      return;
    }

    setTesting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await SettingsService.testTwilioSms(testPhone);
      
      if (response.success) {
        setSuccess(`Test SMS sent to ${testPhone} successfully!`);
      } else {
        setError(response.error || 'Failed to send test SMS');
      }
    } catch (err) {
      setError(err.message || 'Failed to send test SMS');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading settings...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          <MessageSquare className="me-2" size={28} />
          Twilio SMS Settings
        </h2>
        <Badge bg="info">SMS Configuration</Badge>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          <AlertTriangle size={18} className="me-2" />
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-4">
          <CheckCircle size={18} className="me-2" />
          {success}
        </Alert>
      )}

      <Row>
        <Col lg={8}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <Server size={18} className="me-2" />
                Twilio Configuration
              </h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Account SID</Form.Label>
                  <Form.Control
                    type="text"
                    name="twilio_account_sid"
                    value={twilioSettings.twilio_account_sid}
                    onChange={handleChange}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                  <Form.Text className="text-muted">
                    Your Twilio Account SID (starts with AC...)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Auth Token</Form.Label>
                  <Form.Control
                    type="password"
                    name="twilio_auth_token"
                    value={twilioSettings.twilio_auth_token}
                    onChange={handleChange}
                    placeholder="•••••••• or your auth token"
                  />
                  <Form.Text className="text-muted">
                    Your Twilio Auth Token (keep this secret!)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="twilio_phone_number"
                    value={twilioSettings.twilio_phone_number}
                    onChange={handleChange}
                    placeholder="+1234567890"
                  />
                  <Form.Text className="text-muted">
                    Your Twilio phone number in E.164 format (e.g. +1234567890)
                  </Form.Text>
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button variant="primary" type="submit" disabled={saving}>
                    {saving ? <Spinner size="sm" /> : <><Save size={16} className="me-2" />Save Settings</>}
                  </Button>
                </div>
              </Form>

              <hr className="my-4" />

              <h6>
                <Send size={16} className="me-2" />
                Test SMS Configuration
              </h6>
              <div className="d-flex gap-2">
                <Form.Control
                  type="tel"
                  placeholder="Enter test phone number (+1234567890)"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                />
                <Button 
                  variant="outline-primary" 
                  onClick={handleTestSms}
                  disabled={testing}
                >
                  {testing ? <Spinner size="sm" /> : <><Send size={16} className="me-2" />Test</>}
                </Button>
              </div>
              <Form.Text className="text-muted">
                Enter a phone number with country code to test SMS delivery
              </Form.Text>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="bg-light">
            <Card.Header>
              <h5 className="mb-0">
                <Phone size={18} className="me-2" />
                Getting Started
              </h5>
            </Card.Header>
            <Card.Body className="small">
              <p><strong>1. Sign up at Twilio:</strong><br/>
              Create a free account at <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer">twilio.com</a></p>

              <p><strong>2. Get your credentials:</strong><br/>
              - Account SID: Found on your dashboard<br/>
              - Auth Token: Click "Show" on your dashboard<br/>
              - Phone Number: Buy a number or use trial number</p>

              <p><strong>3. Configure here:</strong><br/>
              Enter your credentials above and save</p>

              <p><strong>4. Test:</strong><br/>
              Send a test SMS to verify everything works</p>

              <hr/>

              <p className="mb-0"><strong>Note:</strong> Trial accounts can only send SMS to verified numbers. Upgrade to send to any number.</p>
            </Card.Body>
          </Card>

          <Card className="mt-3 bg-warning-light">
            <Card.Header>
              <h5 className="mb-0">
                <AlertTriangle size={18} className="me-2" />
                Security Notice
              </h5>
            </Card.Header>
            <Card.Body className="small">
              <p className="mb-0">Your Twilio credentials are stored securely in the database and are never exposed in the API responses. The Auth Token is masked after saving.</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user
});

export default connect(mapStateToProps)(TwilioSettings);
